import { normalize } from 'normalizr'
import { Schemas } from '@redux/schema'
import { deleteEntity, updateEntities } from '@redux/entities'

import api from '@functions/api'

import type { OrderItemModel, OrderModel } from '@models/types'

import type { AppDispatch } from '@redux/store'
import type {
  Action,
  DeleteParams,
  EntityOptions,
  Errors,
  FetchRequestPayload,
  RootReducerState,
  UpdateParams,
} from '@redux/modules/types'

import { handleError } from '../utils'

const APPROVE_REQUEST = 'realbase/order/APPROVE_REQUEST'
const APPROVE_SUCCESS = 'realbase/order/APPROVE_SUCCESS'
const APPROVE_FAILURE = 'realbase/order/APPROVE_FAILURE'

const FETCH_REQUEST = 'realbase/order/FETCH_REQUEST'
const FETCH_SUCCESS = 'realbase/order/FETCH_SUCCESS'
const FETCH_FAILURE = 'realbase/order/FETCH_FAILURE'

const CREATE_REQUEST = 'realbase/order/CREATE_REQUEST'
const CREATE_SUCCESS = 'realbase/order/CREATE_SUCCESS'
const CREATE_FAILURE = 'realbase/order/CREATE_FAILURE'

const UPDATE_REQUEST = 'realbase/order/UPDATE_REQUEST'
const UPDATE_SUCCESS = 'realbase/order/UPDATE_SUCCESS'
const UPDATE_FAILURE = 'realbase/order/UPDATE_FAILURE'

const DELETE_REQUEST = 'realbase/order/DELETE_REQUEST'
const DELETE_SUCCESS = 'realbase/order/DELETE_SUCCESS'
const DELETE_FAILURE = 'realbase/order/DELETE_FAILURE'

export type OrderModuleState = {
  approving: boolean,
  creating: boolean,
  deleting: boolean,
  errors: string[],
  loaded: boolean,
  loadedForKeys: string[],
  loadedIds: number[],
  loading: boolean,
  updating: boolean,
}

// Initial State
const initialState: OrderModuleState = {
  approving: false,
  creating: false,
  deleting: false,
  errors: [],
  loaded: false,
  loadedForKeys: [],
  loadedIds: [],
  loading: false,
  updating: false,
}

// Actions
export function fetchRequest(payload: FetchRequestPayload = {}) {
  return {
    type: FETCH_REQUEST,
    loadedForKeys: payload.loadedForKeys,
    loadedIds: payload.loadedIds,
  }
}

export function fetchSuccess() {
  return {
    type: FETCH_SUCCESS,
  }
}

export function fetchFailure(errors: Errors = []) {
  return {
    type: FETCH_FAILURE,
    errors,
  }
}

export function createRequest() {
  return {
    type: CREATE_REQUEST,
  }
}

export function createSuccess() {
  return {
    type: CREATE_SUCCESS,
  }
}

export function createFailure(errors: Errors = []) {
  return {
    type: CREATE_FAILURE,
    errors,
  }
}

export function updateRequest() {
  return {
    type: UPDATE_REQUEST,
  }
}

export function updateSuccess() {
  return {
    type: UPDATE_SUCCESS,
  }
}

export function updateFailure(errors: Errors = []) {
  return {
    type: UPDATE_FAILURE,
    errors,
  }
}

export function approveRequest() {
  return {
    type: APPROVE_REQUEST,
  }
}

export function approveSuccess() {
  return {
    type: APPROVE_SUCCESS,
  }
}

export function approveFailure(errors = []) {
  return {
    type: APPROVE_FAILURE,
    errors,
  }
}

export function deleteRequest() {
  return {
    type: DELETE_REQUEST,
  }
}

export function deleteSuccess() {
  return {
    type: DELETE_SUCCESS,
  }
}

export function deleteFailure(errors: Errors = []) {
  return {
    type: DELETE_FAILURE,
    errors,
  }
}

// Action Creators
export function loadOrders(options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const loadedForKeys = [...getState().orders.loadedForKeys]

    if (entityKey && !loadedForKeys.includes(entityKey)){
      loadedForKeys.push(entityKey)
    }

    dispatch(fetchRequest({ loadedForKeys }))

    const promise = api('/orders.json', options)
      .then(({ data }: { data: OrderModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function loadOrder(orderId: number | string, options: EntityOptions = {}) {
  const { entityKey } = options

  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const loadedForKeys = [...state.orders.loadedForKeys]
    const loadedIds = [...state.portals.loadedIds]

    if (entityKey) loadedForKeys.push(entityKey)
    // @ts-expect-error TS2345
    if (orderId && !loadedIds.includes(orderId)) loadedIds.push(orderId)

    // Set Loading
    dispatch(fetchRequest({ loadedForKeys, loadedIds }))

    const promise = api(`/orders/${orderId}.json`, options)
      .then(({ data }: { data: OrderModel}) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(fetchSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(fetchFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createOrder(
  order: Partial<OrderModel>,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      order,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/orders.json', options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createOrders(
  payload: object,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      order: payload,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/orders/create_multiple.json', options, config)
      .then(({ data }: { data: OrderModel[] }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function addItemToOrder(
  existingOrderId: number,
  itemParams: object,
  options: EntityOptions = {},
) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      order: itemParams,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api(`/orders/${existingOrderId}/item.json`, options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function createOrderWithOrderItem(orderItem: OrderItemModel, options: EntityOptions = {}) {
  const config = {
    method: 'POST',
    data: JSON.stringify({
      order_item_id: orderItem.id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(createRequest())

    const promise = api('/orders/create_from_order_item.json', options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(createSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(createFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateOrder(order: UpdateParams<OrderModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/orders/${order.id}.json`, options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function updateOrderTotal(
  orderId: number,
  total: number,
) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { orders } = state.entities

    const order = orders[orderId]

    if (order){
      const updatedOrder = { ...order, total }
      const normalizedJson = normalize(updatedOrder, Schemas.ORDER)
      // @ts-expect-error TS2345
      dispatch(updateEntities(normalizedJson))
      dispatch(updateSuccess())
    } else {
      dispatch(updateFailure(['Order does not exist.']))
    }
  }
}

export function bulkStatusUpdate(order: UpdateParams<OrderModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      status_id: order.status_id,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/orders/${order.id}/bulk_status_update.json`, options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

type BulkReconcileParams = {
  order: Partial<OrderModel>,
  orderIds: number[],
}

export function bulkReconcileOrders(orderParams: BulkReconcileParams, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order_ids: orderParams.orderIds,
      order: orderParams.order,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api('/orders/bulk_reconcile.json', options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER_ARRAY)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function approveOrder(order: Partial<OrderModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
  }

  return (dispatch: AppDispatch) => {
    dispatch(approveRequest())

    const promise = api(`/orders/${order.id}/approve.json`, options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(approveSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        // @ts-expect-error TS2345
        dispatch(approveFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function unapproveOrder(order: Partial<OrderModel>, options: EntityOptions = {}) {
  const config = {
    method: 'PUT',
    data: JSON.stringify({
      order,
    }),
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/orders/${order.id}/unapprove.json`, options, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function reprocessAPI(order: Partial<OrderModel>) {
  const config = {
    method: 'POST',
  }

  return (dispatch: AppDispatch) => {
    dispatch(updateRequest())

    const promise = api(`/orders/${order.id}/reprocess_api_job.json`, {}, config)
      .then(({ data }: { data: OrderModel }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(updateEntities(normalizedJson))
        dispatch(updateSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(updateFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function deleteOrder(order: DeleteParams<OrderModel>) {
  const config = {
    method: 'DELETE',
  }

  return (dispatch: AppDispatch) => {
    dispatch(deleteRequest())

    const promise = api(`/orders/${order.id}.json`, {}, config)
      .then(({ data }: { data: { id: number } }) => {
        const normalizedJson = normalize(data, Schemas.ORDER)
        // @ts-expect-error TS2345
        dispatch(deleteEntity(normalizedJson))
        dispatch(deleteSuccess())

        return { success: true as const, data }
      })
      .catch((data) => {
        const errors = handleError(data)
        dispatch(deleteFailure(errors))

        return { success: false as const, errors }
      })

    return promise
  }
}

export function removeOrderById(orderId: number) {
  return (dispatch: AppDispatch, getState: () => RootReducerState) => {
    const state = getState()
    const { orders } = state.entities
    const order = orders[orderId]

    if (order){
      const normalizedJson = normalize(order, Schemas.ORDER)
      // @ts-expect-error TS2345
      dispatch(deleteEntity(normalizedJson))
    } else {
      console.error('Could not remove undefined order.')
    }
  }
}

// Reducer
export default function reducer(
  state: OrderModuleState = initialState,
  action: Action = { type: '' },
): OrderModuleState {
  switch (action.type){
    case FETCH_REQUEST:
      return {
        ...state,
        loadedForKeys: action.loadedForKeys || state.loadedForKeys,
        loadedIds: action.loadedIds || state.loadedIds,
        loading: true,
      }
    case FETCH_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        errors: [],
      }
    case FETCH_FAILURE:
      return {
        ...state,
        loading: false,
        loaded: false,
        errors: action.errors || [],
      }
    case CREATE_REQUEST:
      return {
        ...state,
        creating: true,
      }
    case CREATE_SUCCESS:
      return {
        ...state,
        creating: false,
        errors: [],
      }
    case CREATE_FAILURE:
      return {
        ...state,
        creating: false,
        errors: action.errors || [],
      }
    case UPDATE_REQUEST:
      return {
        ...state,
        updating: true,
      }
    case UPDATE_SUCCESS:
      return {
        ...state,
        updating: false,
        errors: [],
      }
    case UPDATE_FAILURE:
      return {
        ...state,
        updating: false,
        errors: action.errors || [],
      }
    case APPROVE_REQUEST:
      return {
        ...state,
        approving: true,
      }
    case APPROVE_SUCCESS:
      return {
        ...state,
        approving: false,
        errors: [],
      }
    case APPROVE_FAILURE:
      return {
        ...state,
        approving: false,
        errors: action.errors || [],
      }
    case DELETE_REQUEST:
      return {
        ...state,
        deleting: true,
      }
    case DELETE_SUCCESS:
      return {
        ...state,
        deleting: false,
        errors: [],
      }
    case DELETE_FAILURE:
      return {
        ...state,
        deleting: false,
        errors: action.errors || [],
      }
    default:
      return state
  }
}
